import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import RoadiesShell from './RoadiesShell';
import { GameHead, TopBar, useSaveScore } from './common';
import { useGameTimer, useIsMobile, useRotateNotice } from './hooks';

// Stage 2 maze — generated entirely in code so the walls, astronaut and treats all share one grid.
// The old version drew a static maze-latest.png and moved the astro by pixels, which left the sprite
// oversized and overlapping the painted walls. Here a DFS-carved grid defines the corridors; the
// astro and treats are sized to a cell and move one cell at a time, so nothing can overlap a wall.
// Layout mirrors maze-latest.png (960x560 canvas): a 27x17 maze centred on the canvas, an arrow
// pointing into the entrance on the left-middle and a door at the exit on the right-middle.
const WIDTH = 960;
const HEIGHT = 560;
const CELL = 30; // pixel size of one grid cell
const COLS = 27;
const ROWS = 17;
const OX = Math.round((WIDTH - COLS * CELL) / 2); // maze left offset
const OY = Math.round((HEIGHT - ROWS * CELL) / 2); // maze top offset
const MID = Math.floor(ROWS / 2); // entrance + exit row (same row, like the old art)
const LINE = 3; // wall thickness
const WALL = '#ffffff'; // maze walls (matches the old white maze art on the blue page)
const PATH = '#0b2a6b'; // solid blue panel behind the maze (corridors show this blue, not the page)
const TREAT_COUNT = 3;

// Astro / treat drawn inside a cell with a little padding so they sit cleanly on the path.
const SPRITE_PAD = 4;
const SPRITE = CELL - SPRITE_PAD * 2;
// astro keeps its 90:130 portrait aspect, fit to the cell height
const ASTRO_H = CELL - 6;
const ASTRO_W = Math.round(ASTRO_H * (90 / 130));

// ---- maze generation (recursive-backtracker / DFS) -----------------------------------------
// grid[r][c] holds wall booleans for each side. We carve passages between cells.
function buildMaze(cols, rows) {
  const cells = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => ({ top: true, right: true, bottom: true, left: true, visited: false })),
  );
  const stack = [];
  let r = 0;
  let c = 0;
  cells[r][c].visited = true;
  stack.push([r, c]);

  const neighbours = (cr, cc) => {
    const list = [];
    if (cr > 0 && !cells[cr - 1][cc].visited) list.push(['top', cr - 1, cc]);
    if (cc < cols - 1 && !cells[cr][cc + 1].visited) list.push(['right', cr, cc + 1]);
    if (cr < rows - 1 && !cells[cr + 1][cc].visited) list.push(['bottom', cr + 1, cc]);
    if (cc > 0 && !cells[cr][cc - 1].visited) list.push(['left', cr, cc - 1]);
    return list;
  };

  while (stack.length) {
    const [cr, cc] = stack[stack.length - 1];
    const options = neighbours(cr, cc);
    if (!options.length) {
      stack.pop();
      continue;
    }
    const [dir, nr, nc] = options[Math.floor(Math.random() * options.length)];
    // knock down the wall between current and chosen neighbour
    if (dir === 'top') {
      cells[cr][cc].top = false;
      cells[nr][nc].bottom = false;
    } else if (dir === 'right') {
      cells[cr][cc].right = false;
      cells[nr][nc].left = false;
    } else if (dir === 'bottom') {
      cells[cr][cc].bottom = false;
      cells[nr][nc].top = false;
    } else {
      cells[cr][cc].left = false;
      cells[nr][nc].right = false;
    }
    cells[nr][nc].visited = true;
    stack.push([nr, nc]);
  }
  return cells;
}

// Can the astro step from (c,r) in a direction? Blocked by that cell's wall or the grid edge.
function canMove(cells, c, r, dir) {
  const cell = cells[r][c];
  if (dir === 'up') return !cell.top && r > 0;
  if (dir === 'down') return !cell.bottom && r < ROWS - 1;
  if (dir === 'left') return !cell.left && c > 0;
  if (dir === 'right') return !cell.right; // right edge of the last column is the exit
  return false;
}

function Stage2Game() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const game = useSelector((s) => s.roadies.game);
  const mobile = useIsMobile();
  const rotate = useRotateNotice();
  const timer = useGameTimer(true, game?.time);
  const save = useSaveScore();

  const base = game?.totalScore || 0; // cumulative total coming into this stage
  const canvasRef = useRef(null);
  const [points, setPoints] = useState(base);
  const [showGif, setShowGif] = useState(false);

  // Mutable game state shared by the draw loop and the key / button handlers.
  const cellsRef = useRef(null);
  const col = useRef(0); // astro grid position
  const row = useRef(MID);
  const itemsRef = useRef([]); // [{c, r, got}]
  const pointsRef = useRef(base);
  const ended = useRef(false);
  const sent = useRef(false);
  const gifTimeout = useRef(null);
  const moveRef = useRef(() => {});

  const finish = async () => {
    if (sent.current) return;
    sent.current = true;
    ended.current = true;
    const state = await save(2, pointsRef.current, timer.current());
    if (state) navigate({ pathname: '/stage3', search });
    else sent.current = false;
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    const astro = new Image();
    const treat = new Image();
    const wrong = new Audio('/roadies/maze_assets/wrong.mp3');
    astro.src = '/roadies/maze_assets/astro.png';
    treat.src = '/roadies/maze_assets/vadapav.png';

    // Build the maze and open the entrance (left) and the exit (right) on the middle row.
    const cells = buildMaze(COLS, ROWS);
    cells[MID][0].left = false;
    cells[MID][COLS - 1].right = false;
    cellsRef.current = cells;

    // Place three treats on random cells that aren't the start cell and aren't on the exit column.
    const placed = [];
    const taken = new Set([`${col.current},${row.current}`]);
    let guard = 0;
    while (placed.length < TREAT_COUNT && guard < 2000) {
      guard += 1;
      const c = Math.floor(Math.random() * (COLS - 1)); // keep off the exit column
      const r = Math.floor(Math.random() * ROWS);
      const key = `${c},${r}`;
      if (taken.has(key)) continue;
      taken.add(key);
      placed.push({ c, r, got: false });
    }
    itemsRef.current = placed;

    let raf;

    const flashGif = () => {
      setShowGif(true);
      clearTimeout(gifTimeout.current);
      gifTimeout.current = setTimeout(() => setShowGif(false), 3000);
    };

    // ---- draw --------------------------------------------------------------------------------
    function drawWalls() {
      ctx.strokeStyle = WALL;
      ctx.lineWidth = LINE;
      ctx.lineCap = 'round';
      ctx.beginPath();
      for (let r = 0; r < ROWS; r += 1) {
        for (let c = 0; c < COLS; c += 1) {
          const cell = cells[r][c];
          const px = OX + c * CELL;
          const py = OY + r * CELL;
          if (cell.top) {
            ctx.moveTo(px, py);
            ctx.lineTo(px + CELL, py);
          }
          if (cell.left) {
            ctx.moveTo(px, py);
            ctx.lineTo(px, py + CELL);
          }
          // right / bottom only on the outer edges — inner walls are covered by neighbours' top/left
          if (c === COLS - 1 && cell.right) {
            ctx.moveTo(px + CELL, py);
            ctx.lineTo(px + CELL, py + CELL);
          }
          if (r === ROWS - 1 && cell.bottom) {
            ctx.moveTo(px, py + CELL);
            ctx.lineTo(px + CELL, py + CELL);
          }
        }
      }
      ctx.stroke();
    }

    // white arrow pointing into the entrance
    function drawArrow() {
      const y = OY + MID * CELL + CELL / 2;
      const x1 = OX - 70;
      const x2 = OX - 14;
      ctx.strokeStyle = WALL;
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
      ctx.moveTo(x2 - 12, y - 10);
      ctx.lineTo(x2, y);
      ctx.lineTo(x2 - 12, y + 10);
      ctx.stroke();
    }

    // grey door standing in the exit gap
    function drawDoor() {
      const x = OX + COLS * CELL + 6;
      const y = OY + MID * CELL + CELL / 2 - 44;
      ctx.fillStyle = '#c9ccd1';
      ctx.fillRect(x, y, 34, 88);
      ctx.strokeStyle = WALL;
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, 34, 88);
      ctx.fillStyle = '#8e939b';
      ctx.fillRect(x + 7, y + 8, 20, 72);
    }

    function draw() {
      ctx.clearRect(0, 0, WIDTH, HEIGHT);
      const bg = ctx.createLinearGradient(0, 0, 0, HEIGHT);
      bg.addColorStop(0, '#0a3a8a');
      bg.addColorStop(1, '#02205f');
      ctx.fillStyle = bg; // blue panel behind the maze
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
      drawWalls();
      drawArrow();
      drawDoor();

      // treats, centred in their cell
      itemsRef.current.forEach((it) => {
        if (it.got) return;
        const ix = OX + it.c * CELL + (CELL - SPRITE) / 2;
        const iy = OY + it.r * CELL + (CELL - SPRITE) / 2;
        if (treat.complete && treat.naturalWidth) ctx.drawImage(treat, ix, iy, SPRITE, SPRITE);
      });

      // astro, centred in its cell
      if (astro.complete && astro.naturalWidth) {
        const ax = OX + col.current * CELL + (CELL - ASTRO_W) / 2;
        const ay = OY + row.current * CELL + (CELL - ASTRO_H) / 2;
        // soft light disc so the astro stands out on the dark blue path
        ctx.fillStyle = 'rgba(120, 200, 255, 0.35)';
        ctx.beginPath();
        ctx.arc(OX + col.current * CELL + CELL / 2, OY + row.current * CELL + CELL / 2, CELL / 2 - 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.drawImage(astro, ax, ay, ASTRO_W, ASTRO_H);
      }

      raf = requestAnimationFrame(draw);
    }

    // ---- pickups & win ----------------------------------------------------------------------
    function checkPickups() {
      itemsRef.current.forEach((it) => {
        if (!it.got && it.c === col.current && it.r === row.current) {
          it.got = true;
          pointsRef.current += 1;
          setPoints(pointsRef.current);
          flashGif();
        }
      });
    }

    const playWrong = () => {
      try {
        wrong.currentTime = 0;
        wrong.play();
      } catch {
        /* autoplay may be blocked until the first interaction */
      }
    };

    // ---- movement: one cell per press, grid collision -------------------------------------
    function move(dir) {
      if (ended.current) return;
      // reaching past the right edge of the last column is the exit
      if (dir === 'right' && col.current === COLS - 1 && row.current === MID && canMove(cells, col.current, row.current, 'right')) {
        ended.current = true;
        col.current = COLS; // step out into the doorway
        flashGif();
        setTimeout(() => finishRef.current(), 3000);
        return;
      }
      if (!canMove(cells, col.current, row.current, dir)) {
        playWrong();
        return;
      }
      if (dir === 'up') row.current -= 1;
      else if (dir === 'down') row.current += 1;
      else if (dir === 'left') col.current -= 1;
      else if (dir === 'right') col.current += 1;
      checkPickups();
    }
    moveRef.current = move;

    const onKey = (e) => {
      const map = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };
      if (map[e.key]) {
        e.preventDefault();
        move(map[e.key]);
      }
    };
    window.addEventListener('keydown', onKey);

    draw();

    return () => {
      window.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
      clearTimeout(gifTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {rotate && <div className="rd-rotate">Please rotate your device for the best experience</div>}
      <TopBar time={timer.text} points={points}>
        <button type="button" className="rd-link-btn" onClick={finish}>Skip</button>
      </TopBar>
      <GameHead
        title="Ireland"
        highlight="Help Grace drive away the negative energies to welcome a positive New Year in this maze game"
        note={mobile ? 'Use the on-screen arrow buttons to move.' : 'Navigate the maze with the arrow keys.'}
      >
        Irish people also bang bread against the walls and doors to chase away bad luck and evil spirits.
      </GameHead>
      <div className="rd-maze-wrap">
        <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} className="rd-maze-canvas" />
        {/* On-screen controls only on touch / small screens; desktop uses the keyboard arrows. */}
        {mobile && (
          <div className="rd-maze-pad">
            <button type="button" className="rd-maze-key rd-maze-up" onClick={() => moveRef.current('up')} aria-label="Up">▲</button>
            <button type="button" className="rd-maze-key rd-maze-left" onClick={() => moveRef.current('left')} aria-label="Left">◀</button>
            <button type="button" className="rd-maze-key rd-maze-down" onClick={() => moveRef.current('down')} aria-label="Down">▼</button>
            <button type="button" className="rd-maze-key rd-maze-right" onClick={() => moveRef.current('right')} aria-label="Right">▶</button>
          </div>
        )}
      </div>
      {showGif && <img src="/roadies/media/you_got_this.gif" className="rd-maze-gif" alt="You got this" />}
    </>
  );
}

export default function RoadiesStage2() {
  return (
    <RoadiesShell bg="ireland.jpg" bgMob="ireland-mob.jpg" stage={2} className="rd-stage rd-stage2">
      <Stage2Game />
    </RoadiesShell>
  );
}
